import {
  DEFAULT_VALUES,
  KODEM_PUBLIC_REGISTRY,
  KODEM_REGISTRY_HOST,
  KODEM_REGISTRY_USERNAME,
  REGCRED_DOCKER_JSON_FILENAME
} from "@/pages/DeploymentsPage/const"

import type { DockerPlatform, KortexImageName } from "./container-image.types"
import type { DeployCommandLine } from "./deployment-command.types"

// An image that adds the Kodem sensors (/opt/kodem) to the user's image, from
// the Kodem public registry
export const getKortexImage = (name: KortexImageName, version: string) =>
  `${KODEM_PUBLIC_REGISTRY}/${name}:${version}`

export const getSensorsDockerfileLines = (image: string) =>
  ({
    sensorsComment: {
      text: "# Add the Kodem sensors; the launcher starts your app",
      comment: true
    },
    copy: { text: "COPY" },
    copyFrom: { text: `--from=${image}` },
    copyPaths: { text: "/opt/kodem /opt/kodem" },
    entrypoint: {
      text: 'ENTRYPOINT ["/opt/kodem/bin/kortex-launcher", "--",'
    },
    entrypointCommand: { text: '"<your original entrypoint>"]' }
  }) satisfies Record<string, DeployCommandLine>

// Dockerfile lines continue with a trailing "\" too: the image reference gets
// a line of its own, the only line that can run past the command box
export const SENSORS_DOCKERFILE_GROUPS: (keyof ReturnType<
  typeof getSensorsDockerfileLines
>)[][] = [
  ["sensorsComment"],
  ["copy", "copyFrom", "copyPaths"],
  ["entrypoint", "entrypointCommand"]
]

export const getImageDefaultValues = () => ({
  key: Date.now().toString(),
  image: DEFAULT_VALUES.IMAGE
})

// Reads the Kodem registry password from the downloaded Docker config.json,
// which is passed on the next script line
const REGISTRY_PASSWORD_COMMAND = `jq -r '.auths["${KODEM_REGISTRY_HOST}"].password'`

// Logs in to the Kodem registry only (the user's own registry login is kept),
// then builds and pushes the image for the platform the service runs
export const getBuildLines = (platform: DockerPlatform) =>
  ({
    loginComment: {
      text: "# Log in to the Kodem registry with the downloaded credentials",
      comment: true
    },
    password: { text: REGISTRY_PASSWORD_COMMAND },
    passwordFile: { text: `${REGCRED_DOCKER_JSON_FILENAME} |` },
    login: { text: `docker login ${KODEM_REGISTRY_HOST}` },
    loginCredentials: {
      text: `--username ${KODEM_REGISTRY_USERNAME} --password-stdin`
    },
    build: {
      text: `docker buildx build --platform ${platform} --push -t "`,
      name: "image",
      suffix: '" .'
    }
  }) satisfies Record<string, DeployCommandLine>

export const BUILD_GROUPS: (keyof ReturnType<typeof getBuildLines>)[][] = [
  ["loginComment"],
  ["password", "passwordFile", "login", "loginCredentials"],
  ["build"]
]
